/**
 * UTM Tracker - Captures and stores UTM parameters in localStorage
 * Captures: utm_source, utm_medium, utm_content, utm_campaign, gclid, fbclid
 */

/**
 * Store UTM parameters from URL to localStorage
 * Only stores if parameter exists and only on first touch (if not already stored)
 */
export const captureUtmParameters = () => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  // List of UTM parameters to track
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_content',
    'utm_campaign',
    'gbraid',
  ];
  
  // Store each UTM parameter if present
  utmParams.forEach(param => {
    const value = params.get(param);
    if (value && !localStorage.getItem(`first_touch_${param}`)) {
      localStorage.setItem(`first_touch_${param}`, value);
    }
  });
  
  // Handle ad tracking parameters (gclid for Google, fbclid for Facebook)
  // Only store one of them as they shouldn't coexist
  const gclid = params.get('gclid');
  const fbclid = params.get('fbclid');
  
  if (gclid && !localStorage.getItem('first_touch_gclid')) {
    localStorage.setItem('first_touch_gclid', gclid);
  } else if (fbclid && !localStorage.getItem('first_touch_fbclid')) {
    localStorage.setItem('first_touch_fbclid', fbclid);
  }
};

/**
 * Get all stored UTM parameters from localStorage
 * @returns {Object} Object containing all UTM parameters
 */
export const getUtmParameters = () => {
  return {
    utm_source: localStorage.getItem('first_touch_utm_source'),
    utm_medium: localStorage.getItem('first_touch_utm_medium'),
    utm_content: localStorage.getItem('first_touch_utm_content'),
    utm_campaign: localStorage.getItem('first_touch_utm_campaign'),
    gclid: localStorage.getItem('first_touch_gclid'),
    fbclid: localStorage.getItem('first_touch_fbclid'),
    gbraid: localStorage.getItem('first_touch_gbraid'),
  };
}

/**
 * Build a `first_touch_*` prefixed URLSearchParams from stored UTM parameters,
 * for appending to backend API calls (e.g. social login) that need first-touch attribution.
 * @param {Object} utmParameters - result of getUtmParameters()
 * @returns {URLSearchParams}
 */
export const buildFirstTouchQueryParams = (utmParameters) => {
  const queryParams = new URLSearchParams();
  const paramNameByUtmKey = {
    utm_source: 'first_touch_utm_source',
    utm_medium: 'first_touch_utm_medium',
    utm_campaign: 'first_touch_utm_campaign',
    utm_content: 'first_touch_utm_content',
    gclid: 'first_touch_gclid',
    fbclid: 'first_touch_fbclid',
    gbraid: 'first_touch_gbraid',
  };

  Object.entries(paramNameByUtmKey).forEach(([utmKey, paramName]) => {
    if (utmParameters[utmKey]) {
      queryParams.append(paramName, utmParameters[utmKey]);
    }
  });

  return queryParams;
};

/**
 * Set utm_campaign/gclid/fbclid/gbraid on a URLSearchParams from the stored
 * first-touch values, when present. Used to carry first-touch attribution
 * forward onto onboarding page URLs.
 * @param {URLSearchParams} searchParams
 * @returns {URLSearchParams} the same instance, for chaining
 */
export const applyStoredFirstTouchUtmParams = (searchParams) => {
  const campaign = localStorage.getItem('first_touch_utm_campaign');
  if (campaign) searchParams.set('utm_campaign', campaign);

  const gclid = localStorage.getItem('first_touch_gclid');
  if (gclid) searchParams.set('gclid', gclid);

  const fbclid = localStorage.getItem('first_touch_fbclid');
  if (fbclid) searchParams.set('fbclid', fbclid);

  const gbraid = localStorage.getItem('first_touch_gbraid');
  if (gbraid) searchParams.set('gbraid', gbraid);

  return searchParams;
};;